import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Favorite as HealthIcon,
  Close as DeselectIcon,
  RotateRight as RotateIcon,
  Circle as TokenIcon,
  Chair as ObjectsIcon,
  LocalHospital as StatusIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
  RemoveCircleOutline as RemoveIcon,
  MenuBook as StatBlockIcon,
} from '@mui/icons-material';
import { ContextualToolbar, ToolbarButton, ToolbarDivider } from '../shared/StyledComponents';
import ToggleModeButtons from '../shared/ToggleModeButtons';
import { CURSOR_MODE } from '../../constants/gameConstants';

const CursorToolbar = ({ 
  cursorMode,
  onCursorModeChange,
  canPaint,
  selectedCoin,
  selectedObject,
  onEditCharacter,
  onEditHealth,
  onEditStatus,
  onDeselectCoin,
  onCopyCoin,
  onDeleteCoin,
  onToggleHidden,
  onRemoveFromGrid,
  onRotateObject,
  onDeleteObject,
  onOpenMonsterStatBlock,
  canManageEnemies,
  isTokenMode = false,
}) => {
  const cursorModes = [
    { value: CURSOR_MODE.GAME, label: 'Token Layer', icon: TokenIcon, tooltip: 'Token Layer - Select and move tokens' },
    { value: CURSOR_MODE.MAP, label: 'Map Layer', icon: ObjectsIcon, tooltip: 'Map Layer - Select and move objects' },
  ];

  // In token mode, we're always working with tokens (GAME cursor mode)
  const showTokenMode = isTokenMode || cursorMode === CURSOR_MODE.GAME;
  const showMapMode = !isTokenMode && cursorMode === CURSOR_MODE.MAP;

  return (
    <ContextualToolbar>
      {/* Mode selector - only visible if canPaint is true AND not in token mode */}
      {canPaint && !isTokenMode && (
        <>
          <ToggleModeButtons
            modes={cursorModes}
            activeMode={cursorMode}
            onChange={onCursorModeChange}
          />
          <ToolbarDivider />
        </>
      )}

      {selectedCoin && showTokenMode ? (
        <>
          {/* Left side - Edit actions */}
          <ToolbarButton
            variant="outlined"
            startIcon={<HealthIcon />}
            onClick={onEditHealth}
            disabled={selectedCoin.isEnemy && !canManageEnemies}
          >
            Health
          </ToolbarButton>

          <ToolbarButton
            variant="outlined"
            startIcon={<StatusIcon />}
            onClick={onEditStatus}
            disabled={selectedCoin.isEnemy && !canManageEnemies}
          >
            Statuses
          </ToolbarButton>

          <ToolbarButton
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEditCharacter}
            disabled={selectedCoin.isEnemy && !canManageEnemies}
          >
            Character
          </ToolbarButton>

          {selectedCoin.isEnemy && (
            <Tooltip
              title="Full monster stat block: armor, hit points, abilities, traits, actions, and other SRD details"
              arrow
            >
              <span>
                <ToolbarButton
                  variant="outlined"
                  color="secondary"
                  startIcon={<StatBlockIcon />}
                  onClick={() => onOpenMonsterStatBlock?.(selectedCoin)}
                >
                  Stat block
                </ToolbarButton>
              </span>
            </Tooltip>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Right side - Deselect, Copy, Delete */}
          <ToolbarButton
            variant="outlined"
            startIcon={<DeselectIcon />}
            onClick={onDeselectCoin}
          >
            Deselect
          </ToolbarButton>

          <ToolbarButton
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={() => onCopyCoin?.(selectedCoin)}
            disabled={selectedCoin.isEnemy && !canManageEnemies}
          >
            Copy
          </ToolbarButton>

          {/* Remove from grid - only shown if coin is on the grid */}
          {!selectedCoin.removed && (
            <ToolbarButton
              variant="outlined"
              startIcon={<RemoveIcon />}
              onClick={() => onRemoveFromGrid?.(selectedCoin.id)}
              disabled={selectedCoin.isEnemy && !canManageEnemies}
            >
              Remove
            </ToolbarButton>
          )}

          {/* Hide/Show toggle */}
          <ToolbarButton
            variant="outlined"
            startIcon={selectedCoin.hidden ? <ShowIcon /> : <HideIcon />}
            onClick={() => onToggleHidden?.(selectedCoin.id)}
            disabled={selectedCoin.isEnemy && !canManageEnemies}
          >
            {selectedCoin.hidden ? 'Show' : 'Hide'}
          </ToolbarButton>

          <ToolbarButton
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => onDeleteCoin?.(selectedCoin)}
            disabled={selectedCoin.isEnemy && !canManageEnemies}
          >
            Delete
          </ToolbarButton>
        </>
      ) : selectedObject && showMapMode ? (
        <>
          {/* Object selected */}
          <ToolbarButton
            variant="outlined"
            startIcon={<RotateIcon />}
            onClick={onRotateObject}
          >
            Rotate (90°)
          </ToolbarButton>

          <Box sx={{ flexGrow: 1 }} />

          <ToolbarButton
            variant="outlined"
            startIcon={<DeselectIcon />}
            onClick={() => onDeselectCoin?.()} // Reuse deselect handler
          >
            Deselect
          </ToolbarButton>

          <ToolbarButton
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDeleteObject}
          >
            Delete
          </ToolbarButton>

          <Typography sx={{ color: 'text.secondary', fontFamily: 'MedievalSharp', fontSize: '0.85rem', ml: 2 }}>
            Object selected - Rotation: {selectedObject.rotation}°
          </Typography>
        </>
      ) : (
        <>
          <Box sx={{ flexGrow: 1 }} />
          <Typography sx={{ color: 'text.secondary', fontFamily: 'MedievalSharp', fontSize: '0.9rem' }}>
            {showTokenMode 
              ? 'Select a token to edit or drag to move'
              : 'Click on a map object to select and move it'}
          </Typography>
        </>
      )}
    </ContextualToolbar>
  );
};

export default CursorToolbar;

